import {Component, inject, OnInit, signal, TemplateRef} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {NgbModal, NgbModalRef, NgbPagination} from '@ng-bootstrap/ng-bootstrap';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {ActivityLogReportPageData, ActivityLogReportService} from '../activity-log-report.service';
import {PageResponse} from '../../../../shared/commons/page-response';
import {ThaiDateTimePipe} from '../../../../shared/commons/thai-datetime.pipe';
import {PageTitleComponent} from '../../../../shared/commons/page-title/page-title.component';
import {PagesizeSelectComponent} from '../../../../shared/components/pagesize-select/pagesize-select.component';
import {GtNgDatepickerComponent} from '../../../../shared/commons/gt-ng-datepicker/gt-ng-datepicker.component';
import {ToastService} from '../../../../shared/commons/toast/toast-service';
import {ConfirmDialogService} from '../../../../shared/ui/confirm-dialog/confirm-dialog.service';
import {LogRetentionSettingService} from '../../../settings/log-retention-setting/log-retention-setting-service';

/** กลุ่มผู้ดำเนินการ (issue #113): ecoding เหลือเพียง ผู้ใช้งาน กับ API */
type OperatorGroupValue = '' | 'USER' | 'API';

/** โหมดของ modal เก็บรักษาข้อมูล: create = บันทึกใหม่จากรายการที่เลือก, view = ดูรายการที่เก็บไว้แล้ว (ยกเลิกได้) */
type KeepModalMode = 'create' | 'view';

@Component({
  selector: 'app-activity-log-report-list',
  standalone: true,
  imports: [FormsModule, NgbPagination, ThaiDateTimePipe, PageTitleComponent, PagesizeSelectComponent, GtNgDatepickerComponent],
  templateUrl: './activity-log-report-list.component.html',
  styleUrl: './activity-log-report-list.component.scss'
})
export class ActivityLogReportListComponent implements OnInit {
  private readonly service = inject(ActivityLogReportService);
  private readonly modalService = inject(NgbModal);
  private readonly toastService = inject(ToastService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly logRetentionService = inject(LogRetentionSettingService);

  protected term = signal<string>('');
  protected operatorGroup = signal<OperatorGroupValue>('');
  protected dateStart = signal<string>('');
  protected dateEnd = signal<string>('');
  protected pageData = signal<PageResponse<ActivityLogReportPageData>>({
    page: {size: 20, number: 0}
  } as PageResponse<ActivityLogReportPageData>);

  protected selectedIds = signal<Set<number>>(new Set());
  protected isAllSelected = signal<boolean>(false);

  // --- modal เก็บรักษาข้อมูล ---
  protected keepMode = signal<KeepModalMode>('create');
  protected keepDetail = signal<string>('');
  protected keepByName = signal<string>('');
  protected keepAt = signal<string>('');
  protected keepSubmitted = signal<boolean>(false);
  protected keepSaving = signal<boolean>(false);
  protected keepTarget = signal<ActivityLogReportPageData | null>(null);
  protected currentUserName = signal<string>('');
  /** จำนวนวันเก็บ Log จากหน้าตั้งค่า (default 90) ใช้แสดงในกล่องหมายเหตุ */
  protected retentionDays = signal<number>(90);
  private keepModalRef: NgbModalRef | null = null;

  ngOnInit(): void {
    this.oidcSecurityService.getUserData().subscribe(userData => {
      this.currentUserName.set(userData?.name ?? userData?.preferred_username ?? '');
    });
    this.logRetentionService.getSetting().subscribe({
      next: (setting) => this.retentionDays.set(setting.retentionDays ?? 90),
      error: () => {}
    });
    this.search();
  }

  search(page: number = 1): void {
    const params: Record<string, string | number> = {
      page: page - 1,
      size: this.pageData().page.size,
      sort: 'id,desc'
    };
    if (this.term().trim()) {
      params['term'] = this.term().trim();
    }
    if (this.operatorGroup()) {
      params['operatorGroup'] = this.operatorGroup();
    }
    if (this.dateStart()) {
      params['dateStart'] = this.dateStart();
    }
    if (this.dateEnd()) {
      params['dateEnd'] = this.dateEnd();
    }
    this.service.search(params).subscribe({
      next: (res) => {
        this.pageData.set(res);
        this.syncSelectAllState();
      },
      error: (err) => console.error(err)
    });
  }

  reset(): void {
    this.term.set('');
    this.operatorGroup.set('');
    this.dateStart.set('');
    this.dateEnd.set('');
    this.search();
  }

  private pageIds(): number[] {
    return (this.pageData().content ?? []).map(r => r.id).filter(id => id != null);
  }

  toggleSelectAll(): void {
    const ids = this.pageIds();
    const current = new Set(this.selectedIds());
    if (this.isAllSelected()) {
      ids.forEach(id => current.delete(id));
    } else {
      ids.forEach(id => current.add(id));
    }
    this.selectedIds.set(current);
    this.syncSelectAllState();
  }

  toggleSelectItem(id: number): void {
    const current = new Set(this.selectedIds());
    if (current.has(id)) {
      current.delete(id);
    } else {
      current.add(id);
    }
    this.selectedIds.set(current);
    this.syncSelectAllState();
  }

  private syncSelectAllState(): void {
    const ids = this.pageIds();
    const current = this.selectedIds();
    this.isAllSelected.set(ids.length > 0 && ids.every(i => current.has(i)));
  }

  isItemSelected(id: number | undefined): boolean {
    return id != null && this.selectedIds().has(id);
  }

  protected operatorTypeLabel(row: ActivityLogReportPageData): string {
    return row.operatorTypeText || '-';
  }

  /** เปิด modal เพื่อเก็บรักษาข้อมูลของรายการที่เลือกไว้ */
  openKeepModal(template: TemplateRef<unknown>): void {
    if (this.selectedIds().size === 0) {
      this.toastService.show({header: 'การแจ้งเตือน', type: 'warning', classname: 'text-dark',
        body: 'กรุณาเลือกรายการกิจกรรมอย่างน้อย 1 รายการ'});
      return;
    }
    this.keepMode.set('create');
    this.keepTarget.set(null);
    this.keepDetail.set('');
    this.keepByName.set(this.currentUserName());
    this.keepAt.set(this.nowLocalDateTime());
    this.keepSubmitted.set(false);
    this.openModal(template);
  }

  /** เปิด modal เพื่อดูข้อมูลการเก็บรักษาของรายการที่เก็บไว้แล้ว (ยกเลิกได้) */
  openKeepDetailModal(template: TemplateRef<unknown>, row: ActivityLogReportPageData): void {
    this.keepMode.set('view');
    this.keepTarget.set(row);
    this.keepDetail.set(row.keepDetail ?? '');
    this.keepByName.set(row.keepByName ?? '-');
    this.keepAt.set(row.keepAt ?? '');
    this.keepSubmitted.set(false);
    this.openModal(template);
  }

  /** วันเวลาปัจจุบันในรูปแบบ yyyy-MM-ddTHH:mm:ss (local) เพื่อให้ thaidatetime pipe แสดงผลได้ */
  private nowLocalDateTime(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  private openModal(template: TemplateRef<unknown>): void {
    this.keepModalRef = this.modalService.open(template, {centered: true, backdrop: 'static', size: 'lg'});
    this.keepModalRef.result.then(() => this.keepModalRef = null, () => this.keepModalRef = null);
  }

  saveKeep(): void {
    this.keepSubmitted.set(true);
    const detail = this.keepDetail().trim();
    if (!detail) {
      return;
    }
    this.keepSaving.set(true);
    this.service.keep({ids: Array.from(this.selectedIds()), keepDetail: detail}).subscribe({
      next: () => {
        this.keepSaving.set(false);
        this.toastService.success('บันทึกข้อมูลสำเร็จ');
        this.keepModalRef?.close('saved');
        this.selectedIds.set(new Set());
        this.search(this.pageData().page.number + 1);
      },
      error: (err) => {
        this.keepSaving.set(false);
        this.toastService.error(err?.error?.message ?? 'เกิดข้อผิดพลาด ไม่สามารถบันทึกข้อมูลได้');
      }
    });
  }

  async cancelKeep(): Promise<void> {
    const target = this.keepTarget();
    if (!target) {
      return;
    }
    const confirmed = await this.confirmDialogService.confirm({
      title: 'ยกเลิกการเก็บรักษาข้อมูล',
      message: 'คุณต้องการยกเลิกการเก็บรักษาข้อมูล?',
      confirmText: 'ยืนยัน',
      cancelText: 'ยกเลิก',
      confirmButtonClass: 'btn-warning'
    });
    if (!confirmed) {
      return;
    }
    this.keepSaving.set(true);
    this.service.cancelKeep(target.id).subscribe({
      next: () => {
        this.keepSaving.set(false);
        this.toastService.success('บันทึกข้อมูลสำเร็จ');
        this.keepModalRef?.close('cancelled');
        this.search(this.pageData().page.number + 1);
      },
      error: (err) => {
        this.keepSaving.set(false);
        this.toastService.error(err?.error?.message ?? 'เกิดข้อผิดพลาด ไม่สามารถบันทึกข้อมูลได้');
      }
    });
  }
}
